import React, { useState, useEffect } from 'react';
import { employeesAPI } from '../services/api';
import PageHeader from '../components/PageHeader';
import Modal from '../components/Modal';
import StatusBadge from '../components/StatusBadge';
import { Plus, Search, Edit2, Trash2, Mail, Phone } from 'lucide-react';
import { toast } from 'react-toastify';

const EMPTY = {
  first_name: '', last_name: '', email: '', phone: '', role: 'Associate',
  hourly_rate: '', hire_date: '', status: 'Active', password: '', password2: '',
};

const ROLES = ['Senior Partner', 'Partner', 'Senior Lawyer', 'Associate', 'Professional Assistant', 'Paralegal', 'Conveyancing Clerk', 'Accountant', 'Secretary', 'Clerk', 'Messenger', 'Admin'];

export default function Employees() {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (roleFilter) params.role = roleFilter;
      const { data } = await employeesAPI.list(params);
      setEmployees(data.results || data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => { load(); }, [search, roleFilter]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!editing && form.password !== form.password2) return toast.error('Passwords do not match');
    setSaving(true);
    try {
      if (editing) {
        const { password, password2, ...rest } = form;
        await employeesAPI.update(editing.id, rest);
        toast.success('Staff updated');
      } else {
        await employeesAPI.create(form);
        toast.success('Staff added');
      }
      setModalOpen(false);
      load();
    } catch (err) {
      const msg = err.response?.data ? JSON.stringify(err.response.data) : 'Failed to save';
      toast.error(msg);
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this staff member?')) return;
    try { await employeesAPI.delete(id); toast.success('Removed'); load(); }
    catch { toast.error('Cannot delete staff with records'); }
  };

  const roleColors = { 'Senior Partner': 'bg-purple-100 text-purple-800', Partner: 'bg-indigo-100 text-indigo-700', 'Senior Lawyer': 'bg-blue-100 text-blue-800', Associate: 'bg-sky-100 text-sky-700', Accountant: 'bg-green-100 text-green-700', Secretary: 'bg-pink-100 text-pink-700' };

  const f = (name) => ({ value: form[name] || '', onChange: e => setForm({ ...form, [name]: e.target.value }) });

  return (
    <div className="p-6">
      <PageHeader
        title="Staff Directory"
        subtitle={`${employees.length} staff members`}
        actions={<button className="btn-primary" onClick={() => { setEditing(null); setForm(EMPTY); setModalOpen(true); }}><Plus size={16} />Add Staff</button>}
      />

      <div className="card mb-5 p-4 flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input className="input pl-9" placeholder="Search staff..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <select className="input sm:w-52" value={roleFilter} onChange={e => setRoleFilter(e.target.value)}>
          <option value="">All Roles</option>
          {ROLES.map(r => <option key={r}>{r}</option>)}
        </select>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {loading ? <div className="col-span-4 text-center py-12 text-gray-400">Loading...</div>
        : employees.length === 0 ? <div className="col-span-4 text-center py-12 text-gray-400">No staff found.</div>
        : employees.map(e => (
          <div key={e.id} className="card p-5 hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between mb-4">
              <div className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg" style={{ background: 'linear-gradient(135deg, #1e3a5f, #3b82f6)' }}>
                {e.first_name[0]}{e.last_name[0]}
              </div>
              <StatusBadge status={e.status} />
            </div>
            <p className="font-semibold text-gray-900">{e.first_name} {e.last_name}</p>
            <span className={`inline-block text-xs px-2 py-0.5 rounded-full mt-1 mb-3 font-medium ${roleColors[e.role] || 'bg-gray-100 text-gray-700'}`}>{e.role}</span>
            <div className="space-y-1 text-xs text-gray-500">
              <div className="flex items-center gap-1.5"><Mail size={11} />{e.email}</div>
              {e.phone && <div className="flex items-center gap-1.5"><Phone size={11} />{e.phone}</div>}
              {e.hourly_rate > 0 && <div className="text-green-700 font-medium">${e.hourly_rate}/hour</div>}
              {e.hire_date && <div>Hired: {e.hire_date}</div>}
            </div>
            <div className="flex gap-1 mt-4 pt-3 border-t border-gray-100">
              <button className="flex-1 text-xs text-blue-600 hover:text-blue-700 border border-blue-200 rounded py-1.5" onClick={() => { setEditing(e); setForm({ ...e, password: '', password2: '' }); setModalOpen(true); }}>
                <Edit2 size={12} className="inline mr-1" />Edit
              </button>
              <button className="p-1.5 text-gray-300 hover:text-red-500 rounded border border-gray-200" onClick={() => handleDelete(e.id)}>
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        ))}
      </div>

      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} title={editing ? 'Edit Staff Member' : 'Add Staff Member'}>
        <form onSubmit={handleSubmit} className="grid grid-cols-2 gap-4">
          <div>
            <label className="label">First Name *</label>
            <input className="input" required {...f('first_name')} />
          </div>
          <div>
            <label className="label">Last Name *</label>
            <input className="input" required {...f('last_name')} />
          </div>
          <div>
            <label className="label">Email *</label>
            <input type="email" className="input" required {...f('email')} />
          </div>
          <div>
            <label className="label">Phone</label>
            <input type="tel" className="input" {...f('phone')} />
          </div>
          <div>
            <label className="label">Role</label>
            <select className="input" {...f('role')}>
              {ROLES.map(r => <option key={r}>{r}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Status</label>
            <select className="input" {...f('status')}>
              {['Active', 'Inactive'].map(s => <option key={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Hourly Rate (R)</label>
            <input type="number" step="0.01" min="0" className="input" {...f('hourly_rate')} />
          </div>
          <div>
            <label className="label">Hire Date</label>
            <input type="date" className="input" {...f('hire_date')} />
          </div>
          {!editing && (
            <>
              <div>
                <label className="label">Password *</label>
                <input type="password" className="input" required {...f('password')} />
              </div>
              <div>
                <label className="label">Confirm Password *</label>
                <input type="password" className="input" required {...f('password2')} />
              </div>
            </>
          )}
          <div className="col-span-2 flex justify-end gap-3 pt-2">
            <button type="button" className="btn-secondary" onClick={() => setModalOpen(false)}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Saving...' : editing ? 'Update' : 'Add Staff'}</button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
